import React from 'react';
import PropTypes from 'prop-types';

import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Link from '@mui/material/Link';
import { Accordion } from '@mui/material';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import FormControl from '@mui/material/FormControl';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {styled} from '@mui/system';

import {concatUrl} from '../helpers/url';
import {postCall} from '../helpers/request';
import GlobalContext from '../helpers/context';
import {checkUserRoles} from '../helpers/authentication';
import ActionButton from '../component/atoms/ActionButton';
import CurationProfileLinkDialog from './CurationProfileLinkDialog';


const StyledBadgePadding = styled(Typography)(({ theme }) =>({
    padding: theme.spacing(0, 1)
}));

const StyledLinkList = styled('ul')({
    paddingLeft: 10
});

const StyledLinkListItem = styled('li')({
    marginBottom: 5,
    overflow:'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
});

const StyledLinkOptions = styled(IconButton)({
    float: 'right',
    width: 20,
    height: 20,
    padding: 0,
    marginLeft: 10
});

const StyledDeleteOutlinedIcon = styled(DeleteOutlinedIcon)({
    width: 20,
    height: 20
});

const StyledEditOutlinedIcon = styled(EditOutlinedIcon)({
    width: 20,
    height: 20
});


class CurationProfileMetadata extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            notes: undefined,
            links: {},
            current_note_text: '',
            selected_link_key: '',
            selected_link_label: '',
            selected_link_url: '',
            linkFormDialogOpen: false
        };

        this.accepted_roles = ['admin', 'manager', 'reviewer'];
        this.save_curation_profile_metadata_url = concatUrl(window.ROOT_URL, 'api', 'curation_profile', 'metadata', 'save');
        this.delete_curation_profile_metadata_link_url = concatUrl(window.ROOT_URL, 'api', 'curation_profile', 'metadata', 'delete_link');
    }

    componentDidMount(){
        this.context.checkUserAccess(this.conditionCallback);
        this.updateMetadata();
    }

    componentDidUpdate(prevProps){
        if(this.props.metadata !== prevProps.metadata){
            this.updateMetadata();
        }
    }

    updateMetadata = () => {
        if (this.props.metadata.notes){
            this.updateNotesState(this.props.metadata.notes);
        }
        if (this.props.metadata.links){
            this.setState({
                links: this.props.metadata.links
            });
        }
    };

    conditionCallback = (user) => {
        return checkUserRoles(user, this.accepted_roles);
    };

    updateNotesState = (notes) => {
        let new_state = {
           notes: notes,
        };
        if (notes.length > -1){
           new_state['current_note_text'] = notes[0].text;
        }
        this.setState(new_state);
    };


    // +++ save curation profile notes +++++++++++++++++++++++++++++++++++++++++++++++

    saveCurationProfileMetadataFailed = () => {
        this.context.displayGlobalError('Backend service failure: Could not save the curation profile notes.');
    };

    saveCurationProfileMetadataDone = (data) => {
        this.context.displayGlobalInfo('The changes were successfully saved');
         if (data.metadata.notes){
             this.updateNotesState(data.metadata.notes);
         }
    };

    saveCurationProfileNotes = () => {
        this.form_data = {
            curation_profile_id: this.props.curation_profile_id,
            workspace_id: this.props.workspace_id,
            note_text: this.state.current_note_text
        };
        postCall(this.save_curation_profile_metadata_url , this.form_data,
            this.saveCurationProfileMetadataDone,
            this.saveCurationProfileMetadataFailed);
    };


    // +++ delete a link +++++++++++++++++++++++++++++++++++++++++++++++

    deleteLinkFailed = () => {
        this.context.displayGlobalError('Backend service failure: Could not delete the link.');
    };

    deleteLinkDone = (data) => {
        this.setState({
            links: data.metadata.links
        });
    };

    deleteLink = () => {
        this.form_data = {
            curation_profile_id: this.props.curation_profile_id,
            workspace_id: this.props.workspace_id,
            link_key: this.state.selected_link_key
        };
        postCall(this.delete_curation_profile_metadata_link_url, this.form_data, this.deleteLinkDone, this.deleteLinkFailed);
    };

    resetLinkSelection = () => {
        this.setState({
            selected_link_key: '',
            selected_link_label: '',
            selected_link_url: '',
        });
    };

    showConfirmationDialog = (event) => {
        this.setState({
            selected_link_key: event.currentTarget.dataset.link_key
        });
        this.context.showConfirmationDialog({
            confirm_dialog_on_close: this.resetLinkSelection,
            confirm_dialog_on_confirm: this.deleteLink,
            confirm_dialog_title: 'Please confirm',
            confirm_dialog_text: `Are you sure you want to delete the link "${event.currentTarget.dataset.link_label}" (${event.currentTarget.dataset.link_url})?`,
            confirm_dialog_button_text: 'delete'
        });
    };

    editLink = (event) => {
        this.setState({
            selected_link_key: event.currentTarget.dataset.link_key,
            selected_link_label: event.currentTarget.dataset.link_label,
            selected_link_url: event.currentTarget.dataset.link_url,
            linkFormDialogOpen: true
        });
    };

    openLinkFormDialog = () => {
        this.resetLinkSelection();
        this.setState({
            linkFormDialogOpen: true
        });
    };

    onLinkDialogDone = (links) => {
        this.setState({
            linkFormDialogOpen: false,
            links: links
        });
        this.resetLinkSelection();
    };

    render() {
        let note_variant = this.state.current_note_text ? 'dot' : 'standard';
        let note_submit_disabled = (this.state.current_note_text === '' && !this.state.notes) || (this.state.notes && this.state.current_note_text === this.state.notes[0].text);
        let links_variant = Object.keys(this.state.links).length ? 'dot' : 'standard';

        return (
            <GlobalContext.Consumer>{() => (
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} data-bt="curation_profile_metadata">
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <Badge color="primary" variant={note_variant}>
                                    <StyledBadgePadding>Notes</StyledBadgePadding>
                                </Badge>
                                <Badge color="primary" variant={links_variant}>
                                    <StyledBadgePadding>Links</StyledBadgePadding>
                                </Badge>
                            </Grid>
                        </Grid>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2}>
                            <Grid size={6}>
                                <FormControl fullWidth={true}>
                                <TextField
                                    id="current_note_text"
                                    label="General notes"
                                    slotProps={{ htmlInput: {'data-bt': 'current_note_text'} }}
                                    multiline
                                    minRows="5"
                                    maxRows="50"
                                    fullWidth
                                    value={this.state.current_note_text}
                                    onChange={(event) => {
                                        this.setState({current_note_text: event.target.value});
                                    }}
                                    margin="normal"
                                    variant="outlined"
                                />
                                </FormControl>
                                <ActionButton disabled={note_submit_disabled} text="Save"
                                              dataBt="save_notes_button"
                                              actionCallback={this.saveCurationProfileNotes} />
                            </Grid>
                            <Grid size={6}>
                                {this.state.links && <StyledLinkList data-bt="link_list">
                                    {Object.keys(this.state.links).map((key, index) =>
                                        <StyledLinkListItem key={index} data-bt="link_list_item">
                                            <StyledLinkOptions
                                                onClick={this.showConfirmationDialog}
                                                data-bt="delete_button"
                                                data-link_key={key}
                                                data-link_label={this.state.links[key][0].label}
                                                data-link_url={this.state.links[key][0].url}
                                                aria-label="delete"
                                                color="secondary"
                                                size="large">
                                                <StyledDeleteOutlinedIcon />
                                            </StyledLinkOptions>
                                            <StyledLinkOptions
                                                onClick={this.editLink}
                                                data-bt="edit_button"
                                                data-link_key={key}
                                                data-link_label={this.state.links[key][0].label}
                                                data-link_url={this.state.links[key][0].url}
                                                aria-label="edit"
                                                color="secondary"
                                                size="large">
                                                <StyledEditOutlinedIcon />
                                            </StyledLinkOptions>
                                            <Link href={this.state.links[key][0].url} data-bt="link" target="_blank">{this.state.links[key][0].label}</Link>
                                        </StyledLinkListItem>
                                    )}
                                </StyledLinkList>}
                                <ActionButton actionCallback={this.openLinkFormDialog}
                                              dataBt="add_link_button" text="Add link" linkStyle={true} />

                                <CurationProfileLinkDialog
                                    dialog_is_open={this.state.linkFormDialogOpen}
                                    on_close={() => {
                                        this.setState({linkFormDialogOpen: false});
                                        this.resetLinkSelection();
                                    }}
                                    on_save={this.onLinkDialogDone}
                                    curation_profile_id={this.props.curation_profile_id}
                                    workspace_id={this.props.workspace_id}
                                    link_key={this.state.selected_link_key}
                                    link_label={this.state.selected_link_label}
                                    link_url={this.state.selected_link_url} />
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            )}
            </GlobalContext.Consumer>
        );
    }
}

CurationProfileMetadata.contextType = GlobalContext;

CurationProfileMetadata.propTypes = {
    curation_profile_id: PropTypes.number.isRequired,
    workspace_id: PropTypes.number.isRequired,
    metadata: PropTypes.object
};

export default CurationProfileMetadata;
